// Master palette for the roguelike art pack.
//
// Derived from the user's two factions (Virgin blue, Chad orange)
// plus a small neutral set shared by civilian NPCs and environment art.
// Follows MortMort's rule: outlines are a DARK SHADE of the dominant
// color, never pure black. That rule yields harmony instead of the
// stamped-cutout look cheap pixel art has.
//
// 24-color master. Each faction draws from ~8 of these for a distinctive
// palette subset. Civilian NPCs use the neutrals.

// ---- Neutrals (8) ---------------------------------------------------
export const NEUTRAL = {
  ink:       '#0b0912', // deepest — only used for eye dots / mouth dots
  darkSlate: '#1c1530', // cool near-black, shared background hue
  slate:     '#2f2742', // core neutral shadow
  stone:     '#4a3f5c', // mid neutral
  cloud:     '#8a8297', // soft highlight
  ivory:     '#f4ebd3', // cream highlight, near-white but warm
  skinMid:   '#d9a278', // default skin tone
  skinShade: '#946847', // skin shadow
} as const;

// ---- Virgin (blue/cyan) — 8 ------------------------------------------
export const VIRGIN = {
  outline:  '#0b1226', // navy outline (dark shade of base blue)
  deep:     '#132652', // deep blue shadow
  base:     '#3b82f6', // shirt base — matches PALETTE.int.shirt
  highlight:'#60a5fa', // shirt highlight
  accent:   '#22d3ee', // cyan scarf
  accentHi: '#67e8f9', // cyan highlight
  rim:      '#a5f3fc', // rim light
  ring:     '#0ea5e9', // foot-ring color
} as const;

// ---- Chad (magenta/pink) — 8 ----------------------------------
export const CHAD = {
  outline:  '#3a0a28', // dark magenta outline (never #000)
  deep:     '#9a1d55', // deep magenta
  base:     '#fb923c', // shirt base — matches PALETTE.ext.shirt (orange kept for brand)
  highlight:'#fdba74',
  accent:   '#ec4899', // neon pink sash
  accentHi: '#f472b6',
  rim:      '#fce7f3',
  ring:     '#e11d48', // foot-ring color
} as const;

// ---- Weapon + UI flashes ---------------------------------------------
export const WEAPON = {
  virginBook:    '#f8fafc', // pale page
  virginCover:   '#1e40af', // book cover
  virginPage:    '#cbd5e1',
  chadPhone:'#1f2937',
  chadGlass:'#cbd5e1',
  chadFlash:'#fde68a',
} as const;

// ---- Faction detection ----------------------------------------------
// Inspect a CharacterColors.shirt hex to classify. Replicates the
// approach hypergrid used — cheap, zero dependencies, and the color
// constants in src/sprites.ts PALETTE are stable references.
export type Faction = 'virgin' | 'chad' | 'civilian';

export function classifyFaction(shirtHex: string): Faction {
  const h = shirtHex.toLowerCase();
  if (h === '#3b82f6' || h === '#1d4ed8') return 'virgin';
  if (h === '#fb923c' || h === '#c2410c') return 'chad';
  return 'civilian';
}

// Pick the palette block for a classified faction.
export function factionPalette(f: Faction): typeof VIRGIN | typeof CHAD | null {
  if (f === 'virgin') return VIRGIN;
  if (f === 'chad') return CHAD;
  return null;
}
